import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Text} from '@Components/text';
import type {DesignMeta, GraphicMeta, GraphicsMetaMapType} from '@Components/social-media/publish-params.types';
import {getPosterPlaceholderImagePreviewURL} from '@Libraries/poster-library';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import styles from './social-media-wizard-design-selector-preview.module.scss';
import {type Active, defaultDropAnimationSideEffects, DndContext, DragOverlay, type DropAnimation, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {horizontalListSortingStrategy, SortableContext} from '@dnd-kit/sortable';
import {SocialMediaPreviewItem} from '@Components/social-media/components/social-media-wizard-design-selector-preview/components/social-media-preview-item/social-media-preview-item';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';

export interface SocialMediaWizardDesignSelectorPreviewProps {
  designsToPublishTo: DesignSelection[];
  onUpdateDesignsToPublishTo?: (designs: DesignSelection[]) => void;
  containerClasses?: string;
}

const containerQuery: ContainerQueryObject = {
  xsmall: {
    minWidth: 0,
    maxWidth: 480,
  },
  small: {
    minWidth: 481,
    maxWidth: 783,
  },
  medium: {
    minWidth: 784,
    maxWidth: 945,
  },
  large: {
    minWidth: 946,
    maxWidth: 1149,
  },
  xlarge: {
    minWidth: 1150,
  },
};

export function SocialMediaWizardDesignSelectorPreview({
  designsToPublishTo,
  onUpdateDesignsToPublishTo = (_designs: DesignSelection[]): void => {},
  containerClasses = '',
}: SocialMediaWizardDesignSelectorPreviewProps): ReactElement<any> {
  const [designsGraphicMetaState, setDesignsGraphicMetaState] = useState<GraphicsMetaMapType>({});
  const previewItemsContainerRef = useRef<HTMLDivElement>(null);
  const [shouldShowLeftArrow, setShouldShowLeftArray] = useState(false);
  const [shouldShowRightArray, setShouldShowRightArray] = useState(false);
  const idPrefix = `design-id`;
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);
  const dragPosterIndex = useRef<number>(0);
  const scrollOffset = 56;
  const [showDiv, setShowDiv] = useState<boolean>(false);
  const [active, setActive] = useState<Active | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const createIdForDesignSelection = ({posterHashedId, pageHashedId}: DesignSelection): string => {
    return `${posterHashedId}_${pageHashedId}`;
  };

  const activeItem = useMemo(() => designsToPublishTo.find((item) => createIdForDesignSelection(item) === active?.id), [active, designsToPublishTo]);
  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0',
        },
      },
    }),
  };

  useEffect((): (() => void) | undefined => {
    if (designsToPublishTo.length > 0) {
      const timer = setTimeout(() => {
        setShowDiv(true);
      }, 100);

      return (): void => {
        clearTimeout(timer);
      };
    }

    return undefined;
  }, [designsToPublishTo]);

  const getExistingDesigns = (): DesignSelection[] => {
    return Object.keys(designsGraphicMetaState)
      .map((posterId) => {
        return Object.keys(designsGraphicMetaState[posterId]).map((pageId) => {
          return {posterHashedId: posterId, pageHashedId: pageId} as DesignSelection;
        });
      })
      .flat();
  };

  const getDesignsAdded = (): DesignSelection[] => {
    const existingDesigns = getExistingDesigns();
    return designsToPublishTo.filter((design) => {
      return !existingDesigns.some((existingDesign) => existingDesign.posterHashedId === design.posterHashedId && existingDesign.pageHashedId === design.pageHashedId);
    });
  };

  const getDesignRemoved = (): DesignSelection[] => {
    const existingDesigns = getExistingDesigns();
    return existingDesigns.filter((existingDesign) => {
      return !designsToPublishTo.some((design) => design.posterHashedId === existingDesign.posterHashedId && design.pageHashedId === existingDesign.pageHashedId);
    });
  };

  const updateStateForDesignsRemoved = (designsRemoved: DesignSelection[]): void => {
    setDesignsGraphicMetaState((prevState) => {
      let newState = prevState;

      designsRemoved.forEach(({posterHashedId, pageHashedId}) => {
        if (newState[posterHashedId]) {
          const {[pageHashedId]: _, ...remainingPages} = newState[posterHashedId];
          newState[posterHashedId] = remainingPages;

          if (Object.keys(newState[posterHashedId]).length === 0) {
            const {[posterHashedId]: _, ...remainingPosters} = newState;
            newState = remainingPosters;
          }
        }
      });

      return newState;
    });
  };

  const getGraphicMeta = async ({posterHashedId, pageHashedId}: DesignSelection, order: number): Promise<GraphicMeta> => {
    const data = (await window.PMW.writeLocal('socialmedia/getDesignMeta', {
      posterId: posterHashedId,
      pageId: pageHashedId,
    })) as DesignMeta;

    return {graphicURL: data.previewUrl, isVideo: data.isVideo, name: data.designName, order};
  };

  const updateGraphicMetaForId = ({posterHashedId, pageHashedId}: DesignSelection, updatedGraphicMeta: GraphicMeta): void => {
    setDesignsGraphicMetaState((prevState) => {
      const newState = {...prevState};
      newState[posterHashedId] ??= {};
      newState[posterHashedId][pageHashedId] = updatedGraphicMeta;
      return newState;
    });
  };

  const getGraphicMetaForAllDesigns = async (designs: DesignSelection[]): Promise<void> => {
    try {
      const promises = designs.map((designSelection, index) => {
        return getGraphicMeta(designSelection, index);
      });

      const results = await Promise.all(promises);

      results.forEach((graphicMeta, index) => {
        updateGraphicMetaForId(designs[index], graphicMeta);
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const updateDesignsGraphicsMetaIfNeeded = (): void => {
    const designsAdded = getDesignsAdded();
    const designsRemoved = getDesignRemoved();
    updateStateForDesignsRemoved(designsRemoved);
    void getGraphicMetaForAllDesigns(designsAdded);
  };

  useEffect(() => {
    updateDesignsGraphicsMetaIfNeeded();
  }, [designsToPublishTo]);

  useDidUpdateEffect(() => {
    handlePreviewItemsScroll();
  }, [designsToPublishTo]);

  const getTextContainer = (): ReactElement<any> => {
    if (isMobileView()) {
      return (
        <div className={styles.mobileTextContainer}>
          <Text className="body-lg-regular content-body" bold val={window.i18next.t('pmwjs_selected_designs')} />
          <OverlayTrigger
            trigger={['hover', 'focus', 'click']}
            overlay={
              <Tooltip id="tooltip-overlay-info" className={`_pointer-events-none ${styles.customTooltip} spacing-m-b-1`} style={{position: 'fixed'}}>
                {window.i18next.t('pmwjs_rearrange_selection')}
              </Tooltip>
            }
            delay={{show: 50, hide: 50}}
            placement="top"
            popperConfig={undefined}
            show={undefined}
            defaultShow={undefined}
            onHide={undefined}
            onToggle={undefined}
            target={undefined}
            flip={undefined}
            offset={undefined}
          >
            <Icon size={IconSize.SIZE_ICON_20} icon="icon-info" />
          </OverlayTrigger>
        </div>
      );
    }

    return (
      <div className={`${styles.textContainer} flex-column-justify-between`}>
        <Text className="body-lg-regular content-body" bold val={window.i18next.t('pmwjs_selected_designs')} />
        <Text className="body-md-regular content-sub-text" val={window.i18next.t('pmwjs_rearrange_selection')} />
      </div>
    );
  };

  const scrollTo = (scrollLeft: number): void => {
    if (previewItemsContainerRef.current) {
      previewItemsContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const onClickRightArrow = (): void => {
    if (previewItemsContainerRef?.current) {
      const newScrollLeft = previewItemsContainerRef.current.scrollLeft + scrollOffset;
      scrollTo(newScrollLeft);
    }
  };

  const getRightScrollArrow = (): ReactElement<any> | null => {
    if (!shouldShowRightArray) {
      return null;
    }

    return (
      <Icon
        icon="icon-caret-right"
        onClick={onClickRightArrow}
        className={`${styles.showMoreArrow} ${styles.right}`}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.CIRCLE}
        type={IconType.SECONDARY}
      />
    );
  };

  const onClickLeftArrow = (): void => {
    if (previewItemsContainerRef?.current) {
      const newScrollLeft = previewItemsContainerRef.current.scrollLeft - scrollOffset;
      scrollTo(newScrollLeft);
    }
  };

  const getLeftScrollArrow = (): ReactElement<any> | null => {
    if (!shouldShowLeftArrow) {
      return null;
    }

    return (
      <Icon
        icon="icon-caret-left"
        onClick={onClickLeftArrow}
        className={`${styles.showMoreArrow} ${styles.left}`}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.CIRCLE}
        type={IconType.SECONDARY}
      />
    );
  };

  const areItemsOverflowing = (): boolean => {
    if (designsToPublishTo.length === 1) {
      return false;
    }

    if (previewItemsContainerRef?.current) {
      if (previewItemsContainerRef.current.scrollWidth > previewItemsContainerRef.current.clientWidth) {
        return true;
      }
    }

    return false;
  };

  const handlePreviewItemsScroll = (): void => {
    if (!areItemsOverflowing()) {
      setShouldShowLeftArray(false);
      setShouldShowRightArray(false);
      return;
    }

    if (previewItemsContainerRef?.current) {
      const {scrollLeft} = previewItemsContainerRef.current;
      const maxScrollLeft = previewItemsContainerRef.current.scrollWidth - (previewItemsContainerRef.current.scrollLeft + previewItemsContainerRef.current.clientWidth);

      if (scrollLeft < 1) {
        setShouldShowLeftArray(false);
      } else {
        setShouldShowLeftArray(true);
      }

      if (maxScrollLeft < 1) {
        setShouldShowRightArray(false);
      } else {
        setShouldShowRightArray(true);
      }
    }
  };

  const getDesignPreviewSource = ({posterHashedId, pageHashedId}: DesignSelection): string => {
    if (designsGraphicMetaState[posterHashedId]?.[pageHashedId] && designsGraphicMetaState[posterHashedId][pageHashedId].graphicURL !== '') {
      return designsGraphicMetaState[posterHashedId][pageHashedId].graphicURL;
    }

    return getPosterPlaceholderImagePreviewURL();
  };

  const isVideoPoster = ({posterHashedId, pageHashedId}: DesignSelection): boolean => {
    return designsGraphicMetaState[posterHashedId]?.[pageHashedId]?.isVideo ?? false;
  };

  const isPosterLoading = ({posterHashedId, pageHashedId}: DesignSelection): boolean => {
    return !(
      designsGraphicMetaState[posterHashedId]?.[pageHashedId] &&
      (designsGraphicMetaState[posterHashedId]?.[pageHashedId]?.graphicURL !== '' || designsGraphicMetaState[posterHashedId]?.[pageHashedId]?.graphicURL !== undefined)
    );
  };

  const getDesignPreviewAsset = (design: DesignSelection): ReactElement<any> => {
    if (!isVideoPoster(design) || isPosterLoading(design)) {
      return <img alt={window.i18next.t('pmwjs_design_thumbnail')} src={getDesignPreviewSource(design)} className={styles.previewImage} />;
    }

    return (
      <>
        <div className={styles.videoIconContainer} aria-hidden="true">
          <Icon className={'content-body-white'} icon={'icon-play-video'} size={IconSize.SIZE_ICON_12} />
        </div>
        <img alt={window.i18next.t('pmwjs_design_thumbnail')} src={getDesignPreviewSource(design)} className={styles.previewImage} />
      </>
    );
  };

  const generateDesignPreviewId = (design: DesignSelection): string => {
    const designSelectionId = createIdForDesignSelection(design);
    return `${idPrefix}_${designSelectionId}`;
  };

  const onClickDelete = (design: DesignSelection): void => {
    const updatedDesigns = designsToPublishTo.filter((designToPublish) => {
      return !(designToPublish.posterHashedId === design.posterHashedId && designToPublish.pageHashedId === design.pageHashedId);
    });

    onUpdateDesignsToPublishTo(updatedDesigns);
  };

  const getHoverOverlay = (design: DesignSelection): ReactElement<any> => {
    return (
      <div className={styles.hoverOverlay}>
        <Icon
          onClick={() => {
            onClickDelete(design);
          }}
          className={styles.deleteIcon}
          shape={IconShape.CIRCLE}
          type={IconType.SECONDARY}
          size={IconSize.SIZE_ICON_16}
          icon="icon-delete"
        />
      </div>
    );
  };

  const getDesignPreviewForId = (index: number): ReactElement<any> => {
    return (
      <div
        onTransitionEnd={handlePreviewItemsScroll}
        key={generateDesignPreviewId(designsToPublishTo[index])}
        className={`${styles.previewWrapper} ${styles.slideIn} ${showDiv ? styles.show : ''}`}
      >
        <div id={generateDesignPreviewId(designsToPublishTo[index])} draggable className={styles.preview}>
          {getDesignPreviewAsset(designsToPublishTo[index])}
        </div>
        {getHoverOverlay(designsToPublishTo[index])}
      </div>
    );
  };

  const onDragEnd = (sourceId: string, destinationId: string): void => {
    const rearrangedDesigns: DesignSelection[] = [...designsToPublishTo];
    const sourceIndex = rearrangedDesigns.findIndex((design) => createIdForDesignSelection(design) === sourceId);
    const destinationIndex = rearrangedDesigns.findIndex((design) => createIdForDesignSelection(design) === destinationId);

    if (sourceIndex === -1 || destinationIndex === -1) {
      return;
    }

    const [movedItem] = rearrangedDesigns.splice(sourceIndex, 1);
    rearrangedDesigns.splice(destinationIndex, 0, movedItem);

    onUpdateDesignsToPublishTo(rearrangedDesigns);
  };

  const onDragStart = (draggedId: string): void => {
    dragPosterIndex.current = parseInt(draggedId, 10);
  };

  const getDesignPreviews = (): ReactElement<any> => {
    const designPreviews: ReactElement<any>[] = [];

    for (let i = 0; i < designsToPublishTo.length; i++) {
      designPreviews.push(getDesignPreviewForId(i));
    }

    return (
      <div onScroll={handlePreviewItemsScroll} ref={previewItemsContainerRef} className={`${styles.previewItems} ${isMobileView() ? styles.mobileContainer : ''}`}>
        <DndContext
          sensors={sensors}
          onDragStart={({active}): void => {
            setActive(active);
            onDragStart(String(active.id));
          }}
          onDragEnd={({active, over}): void => {
            if (!over) {
              return;
            }

            if (active.id === over.id) {
              return;
            }
            onDragEnd(String(active.id), String(over.id));
          }}
          onDragCancel={(): void => {
            setActive(null);
          }}
        >
          <SortableContext strategy={horizontalListSortingStrategy} items={designsToPublishTo.map((design) => createIdForDesignSelection(design))}>
            <div className={styles.draggableClasses}>
              {designPreviews.map((designPreview, index) => {
                return <SocialMediaPreviewItem id={createIdForDesignSelection(designsToPublishTo[index])} item={designPreview} key={`sortable-preview-${index}`} />;
              })}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={dropAnimationConfig}>{activeItem ? getDesignPreviewForId(designsToPublishTo.indexOf(activeItem)) : null}</DragOverlay>
        </DndContext>
      </div>
    );
  };

  const getGradientOverlay = (classes: string, key: string): ReactElement<any> => {
    return <div key={key} className={`${styles.overlay} ${classes}`} />;
  };

  const getClassesForLeftwardGradient = (): string => {
    if (shouldShowLeftArrow) {
      return styles.leftOverlay;
    }

    return '_hidden';
  };

  const getClassesForRightwardGradient = (): string => {
    if (shouldShowRightArray) {
      return styles.rightOverlay;
    }

    return '_hidden';
  };

  const getGradientOverlays = (): ReactElement<any>[] => {
    return [getGradientOverlay(getClassesForLeftwardGradient(), 'gradient-1'), getGradientOverlay(getClassesForRightwardGradient(), 'gradient-2')];
  };

  const getWrapperClasses = (): string => {
    if (params.xsmall) {
      return styles.extraSmall;
    }

    if (params.small) {
      return styles.small;
    }

    if (params.medium) {
      return styles.medium;
    }

    if (params.large) {
      return styles.large;
    }

    if (params.xlarge) {
      return styles.extralarge;
    }

    return '';
  };

  const isMobileView = (): boolean => {
    return params.xsmall || params.small;
  };

  const getPreviewsContainer = (): ReactElement<any> => {
    return (
      <div key="preview-container-key" className={styles.previewsContainer}>
        {getGradientOverlays()}
        {getLeftScrollArrow()}
        {getRightScrollArrow()}
        {getDesignPreviews()}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`${styles.designsContainer} ${getWrapperClasses()} ${containerClasses}`}>
      <div className={`${styles.wrapper} ${getWrapperClasses()}`}>
        {getTextContainer()}
        {getPreviewsContainer()}
      </div>
    </div>
  );
}
